import { useEffect } from 'react';
import type { StorageServiceRoot } from 'src/apis/storage/types';
import { useAxios } from 'src/hooks/use-axios';
import { handleErrorWithNotification } from 'src/utils/handle-error';

export const storageServiceRootCacheKey = 'storage-service-root';

export function useStorageServiceRoot() {
  const storageServiceRoot = useAxios(
    (axios, baseConfig) =>
      axios.request<StorageServiceRoot>({
        ...baseConfig,
        url: PRISMA_CONFIG.storageService,
      }),
    { neededOnPageLoad: true, cacheKey: storageServiceRootCacheKey }
  );

  useEffect(() => {
    storageServiceRoot.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return storageServiceRoot;
}

export type StorageServiceRootRequest = ReturnType<
  typeof useStorageServiceRoot
>;

/**
 * Use this hook, if you need to make a request to Storage Service in a critical path
 * when you don't want the whole page to crash on an error (e.g. in the header component).
 */
export function useOptionalStorageServiceRoot() {
  const storageServiceRoot = useAxios(
    (axios, baseConfig) =>
      axios.request<StorageServiceRoot>({
        ...baseConfig,
        url: PRISMA_CONFIG.storageService,
        timeout: 5000,
      }),
    {
      neededOnPageLoad: true,
      cacheKey: storageServiceRootCacheKey,
      onError(error) {
        // report errors, if available on stage
        handleErrorWithNotification({
          error,
          setReauthenticate: () => {},
        });

        return error;
      },
    }
  );

  useEffect(() => {
    storageServiceRoot.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return storageServiceRoot;
}

export type OptionalStorageServiceRootRequest = ReturnType<
  typeof useOptionalStorageServiceRoot
>;
